import { cosmeticItem, normalizeCosmeticLoadout } from "@craftoff/game-core";
import type { AccountSession } from "./account-api";

export const ACCOUNT_PRESENTATION_KEY = "craftoff-account-presentation-v1";
export const ACCOUNT_PRESENTATION_VERSION = 1;
export const ACCOUNT_PRESENTATION_MAX_AGE_MS = 31 * 24 * 60 * 60 * 1_000;

type StoredAccountSession = Omit<AccountSession, "email">;

export type AccountPresentation = {
  version: typeof ACCOUNT_PRESENTATION_VERSION;
  savedAt: number;
  session: StoredAccountSession;
  wallpaperAssetPath: string;
  customWallpaper: boolean;
};

export type InitialAccount = {
  session: AccountSession | null;
  source: "server" | "cache" | "none";
};

declare global {
  interface Window {
    __CRAFTOFF_BOOTSTRAP__?: unknown;
    __CRAFTOFF_PRESENTATION__?: unknown;
    __CRAFTOFF_APPLY_WALLPAPER__?: (assetPath: string, customWallpaper: boolean) => void;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizedSession(value: unknown): AccountSession | null {
  if (!isRecord(value) || typeof value.playerId !== "string" || typeof value.username !== "string") return null;
  if (!Number.isFinite(value.xp) || typeof value.authenticated !== "boolean" || typeof value.guest !== "boolean" || typeof value.needsUsername !== "boolean") return null;
  if (!isRecord(value.cosmetics)) return null;

  return {
    playerId: value.playerId,
    username: value.username,
    xp: Math.max(0, Math.floor(value.xp as number)),
    rankedRating: Number.isFinite(value.rankedRating) ? Math.max(0, Math.floor(value.rankedRating as number)) : 0,
    progressionVersion: Number.isFinite(value.progressionVersion) ? Math.max(0, Math.floor(value.progressionVersion as number)) : 0,
    authenticated: value.authenticated,
    guest: value.guest,
    needsUsername: value.needsUsername,
    ...(typeof value.email === "string" ? { email: value.email } : {}),
    ...(typeof value.development === "boolean" ? { development: value.development } : {}),
    cosmetics: normalizeCosmeticLoadout({
      wallpaperId: typeof value.cosmetics.wallpaperId === "string" ? value.cosmetics.wallpaperId : undefined,
      craftingTableId: typeof value.cosmetics.craftingTableId === "string" ? value.cosmetics.craftingTableId : undefined,
      faceplateId: typeof value.cosmetics.faceplateId === "string" ? value.cosmetics.faceplateId : undefined
    })
  };
}

export function parseAccountPresentation(value: unknown, now = Date.now()): AccountPresentation | null {
  if (!isRecord(value) || value.version !== ACCOUNT_PRESENTATION_VERSION || !Number.isFinite(value.savedAt)) return null;
  const savedAt = value.savedAt as number;
  if (savedAt > now + 60_000 || now - savedAt > ACCOUNT_PRESENTATION_MAX_AGE_MS) return null;
  const session = normalizedSession(value.session);
  if (!session?.authenticated || session.needsUsername) return null;

  const wallpaper = cosmeticItem(session.cosmetics.wallpaperId);
  const wallpaperAssetPath = wallpaper?.assetPath ?? "/fallback/ui/panorama.png";
  return {
    version: ACCOUNT_PRESENTATION_VERSION,
    savedAt,
    session,
    wallpaperAssetPath,
    customWallpaper: Boolean(wallpaper?.assetPath)
  };
}

export function loadAccountPresentation(): AccountPresentation | null {
  if (typeof window === "undefined") return null;
  try {
    const bootValue = parseAccountPresentation(window.__CRAFTOFF_PRESENTATION__);
    if (bootValue) return bootValue;
    const saved = window.localStorage.getItem(ACCOUNT_PRESENTATION_KEY);
    return saved ? parseAccountPresentation(JSON.parse(saved) as unknown) : null;
  } catch {
    return null;
  }
}

export function loadInitialAccount(): InitialAccount {
  if (typeof window === "undefined") return { session: null, source: "none" };
  const serverSession = normalizedSession(window.__CRAFTOFF_BOOTSTRAP__);
  if (serverSession?.authenticated && !serverSession.needsUsername) return { session: serverSession, source: "server" };
  const cached = loadAccountPresentation();
  return cached ? { session: cached.session, source: "cache" } : { session: null, source: "none" };
}

export function saveAccountPresentation(session: AccountSession): void {
  if (typeof window === "undefined") return;
  if (!session.authenticated || session.needsUsername) {
    clearAccountPresentation();
    return;
  }
  const cosmetics = normalizeCosmeticLoadout(session.cosmetics);
  const wallpaper = cosmeticItem(cosmetics.wallpaperId);
  const presentation: AccountPresentation = {
    version: ACCOUNT_PRESENTATION_VERSION,
    savedAt: Date.now(),
    session: {
      playerId: session.playerId,
      username: session.username,
      xp: Math.max(0, Math.floor(session.xp)),
      rankedRating: Math.max(0, Math.floor(session.rankedRating)),
      progressionVersion: Math.max(0, Math.floor(session.progressionVersion)),
      authenticated: true,
      guest: false,
      needsUsername: false,
      ...(session.development === undefined ? {} : { development: session.development }),
      cosmetics
    },
    wallpaperAssetPath: wallpaper?.assetPath ?? "/fallback/ui/panorama.png",
    customWallpaper: Boolean(wallpaper?.assetPath)
  };
  try {
    window.localStorage.setItem(ACCOUNT_PRESENTATION_KEY, JSON.stringify(presentation));
    window.__CRAFTOFF_PRESENTATION__ = presentation;
  } catch {
    // The verified in-memory account remains active when browser storage is unavailable.
  }
}

export function clearAccountPresentation(): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(ACCOUNT_PRESENTATION_KEY); }
  catch { /* The server session remains authoritative. */ }
  delete window.__CRAFTOFF_PRESENTATION__;
}
